import { useEffect, useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, EllipsisIcon, LayoutTemplateIcon, ListIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BackLink,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  Field,
  ListField,
  LoadingState,
  PageEyebrow,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SelectInput,
  TextArea,
  TextInput,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { NOTE_CATEGORIES, noteCategoryLabel } from '@/lib/events';
import {
  ITEM_FIELD_TYPES,
  TEMPLATE_FIELD_TYPES,
  TEMPLATE_FIELD_TYPE_LABELS,
  createTemplate,
  deleteTemplate,
  listTemplates,
  updateTemplate,
  type Template,
  type TemplateField,
  type TemplateFieldType,
  type TemplateWrite,
} from '@/lib/templates';

/** Sortable columns; keys are flattened row fields. */
const TEMPLATE_SORT_FIELDS = {
  name: 'name',
  appliesTo: 'appliesToText',
  fields: 'fieldCount',
  usage: 'usageCount',
  updated: 'updatedMs',
} as const;
const TEMPLATE_SORT_KEYS = Object.values(TEMPLATE_SORT_FIELDS);
const DEFAULT_TEMPLATE_SORT: DataTableSort = { key: TEMPLATE_SORT_FIELDS.name, direction: 'asc' };

/** A template plus the flattened fields the table sorts and searches on. */
interface TemplateRow extends Template {
  readonly appliesToText: string;
  readonly fieldCount: number;
  readonly fieldsText: string;
  readonly updatedMs: number | null;
}

/** "Charity notes" or "Programs tagged custom". */
function appliesToText(template: Template): string {
  const parts: string[] = [];
  if (template.appliesTo.noteCategory) parts.push(`${noteCategoryLabel(template.appliesTo.noteCategory)} notes`);
  if (template.appliesTo.programTags?.length) parts.push(`Programs tagged ${template.appliesTo.programTags.join(', ')}`);
  return parts.join(' · ') || 'Picked by hand';
}

function templateRow(template: Template): TemplateRow {
  const updatedMs = template.updatedAt ? Date.parse(template.updatedAt) : NaN;
  return {
    ...template,
    appliesToText: appliesToText(template),
    fieldCount: template.fields.length,
    fieldsText: template.fields.map((field) => field.label).join(', '),
    updatedMs: Number.isNaN(updatedMs) ? null : updatedMs,
  };
}

/**
 * The reusable note shapes: which category or program offers each one, its
 * statuses and fields. Admin-only; the API enforces it.
 *
 * @returns The templates list page.
 */
export function TemplatesPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<Template | null>(null);
  const { search, setSearch } = useUrlSearch();
  const { sort, onSort } = useTableSort(TEMPLATE_SORT_KEYS, DEFAULT_TEMPLATE_SORT);

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: ({ signal }) => listTemplates(signal),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: async () => {
      toast.success(`Deleted ${deleting?.name ?? 'template'}`);
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const rows = templates.data
    ? sortRows(filterRows(templates.data.map(templateRow), search, ['name', 'description', 'appliesToText', 'fieldsText']), sort)
    : [];

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ADMINISTRATION"
        title="Note templates"
        subtitle="The shape a note takes for a category or program: a status, the facts to fill in, and a dated log underneath. Notes written without a template keep working as they are."
        action={
          <span data-guide="templates-new">
            <Button variant="primary" onClick={() => navigate('/templates/new')}>
              <PlusIcon aria-hidden className="size-4" /> New template
            </Button>
          </span>
        }
      />

      <div className="mt-6 space-y-4">
        {templates.isPending ? (
          <LoadingState label="Loading templates…" />
        ) : templates.isError ? (
          <ErrorNotice title="Templates unavailable" message={errorMessage(templates.error)} />
        ) : templates.data.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<LayoutTemplateIcon aria-hidden className="size-6" />}
              title="No templates yet"
              message="Create the first shape — a name, the facts a note of this kind always carries, and the statuses it moves through."
              action={
                <Button variant="primary" onClick={() => navigate('/templates/new')}>
                  New template
                </Button>
              }
            />
          </Panel>
        ) : (
          <>
            <SearchInput
              data-guide="templates-search"
              label="Search templates"
              placeholder="Name, category or field"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="max-w-sm"
            />
            <div data-guide="templates-table"><DataTable
              headers={[
                { label: 'Template', sortKey: TEMPLATE_SORT_FIELDS.name },
                { label: 'Applies to', sortKey: TEMPLATE_SORT_FIELDS.appliesTo },
                'Statuses',
                { label: 'Fields', sortKey: TEMPLATE_SORT_FIELDS.fields },
                { label: 'Used by', sortKey: TEMPLATE_SORT_FIELDS.usage },
                { label: 'Updated', sortKey: TEMPLATE_SORT_FIELDS.updated },
                'Actions',
              ]}
              visuallyHiddenHeaders={[6]}
              sort={sort}
              onSort={onSort}
              minWidth={900}
            >
              {rows.map((template) => (
                <tr key={template._id} className="border-t border-portal-line align-middle">
                  <td className="p-3 text-portal-small">
                    <button
                      type="button"
                      onClick={() => navigate(`/templates/${template._id}`)}
                      className="block cursor-pointer text-left text-portal-small font-bold underline-offset-2 hover:underline"
                    >
                      {template.name}
                    </button>
                    <span className="block text-portal-micro text-portal-muted">
                      {template.enabled ? template.key : `${template.key} · disabled`}
                    </span>
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">{template.appliesToText}</td>
                  <td className="p-3 text-portal-small text-portal-muted">
                    {template.status.enabled ? template.status.options.join(', ') : '—'}
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">
                    <span className="block">{template.fieldCount}</span>
                    <span className="block max-w-[280px] truncate text-portal-micro">{template.fieldsText}</span>
                  </td>
                  <td className="p-3 text-portal-small whitespace-nowrap">
                    {template.usageCount === 0 ? (
                      <Pill tone="neutral">Unused</Pill>
                    ) : (
                      <Link
                        to={`/templates/${template._id}/usage`}
                        data-guide="templates-usage"
                        aria-label={`See the ${template.usageCount} note${template.usageCount === 1 ? '' : 's'} that use ${template.name}`}
                        className="rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      >
                        <Pill tone="brand">
                          {template.usageCount} note{template.usageCount === 1 ? '' : 's'}
                        </Pill>
                      </Link>
                    )}
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">
                    {template.updatedMs !== null
                      ? new Date(template.updatedMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </td>
                  <td className="p-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          data-guide="templates-row-menu" aria-label={`Actions for ${template.name}`}
                          className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
                        >
                          <EllipsisIcon aria-hidden className="size-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => navigate(`/templates/${template._id}`)}>
                          <PencilIcon aria-hidden className="size-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => navigate(`/templates/${template._id}/usage`)}>
                          <ListIcon aria-hidden className="size-4" />
                          See where it is used
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(template)}>
                          <XIcon aria-hidden className="size-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </DataTable></div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={deleting !== null}
        tone="danger"
        title={`Delete ${deleting?.name ?? 'this template'}?`}
        message={
          deleting !== null && deleting.usageCount > 0
            ? `${deleting.usageCount} note${deleting.usageCount === 1 ? '' : 's'} follow it. They keep their values; the shape just stops being offered for new notes.`
            : 'No notes reference it.'
        }
        confirmLabel="Delete template"
        onConfirm={() => {
          if (deleting) remove.mutate(deleting._id);
        }}
        onCancel={() => {
          if (!remove.isPending) setDeleting(null);
        }}
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
      />
    </section>
  );
}

/**
 * The create/edit page for one template — a page, not a modal, because a
 * field list with types and options needs the room.
 *
 * @returns The template detail page.
 */
export function TemplateDetailPage(): React.ReactElement {
  const { templateId } = useParams<{ templateId: string }>();
  const isNew = templateId === undefined || templateId === 'new';

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: ({ signal }) => listTemplates(signal),
    enabled: !isNew,
  });

  if (!isNew && templates.isPending) return <LoadingState label="Loading template…" />;
  if (!isNew && templates.isError) {
    return <ErrorNotice title="Template unavailable" message={errorMessage(templates.error)} />;
  }

  const template = isNew ? null : (templates.data?.find((row) => row._id === templateId) ?? null);
  if (!isNew && template === null) {
    return <ErrorNotice title="Template not found" message="It may have been deleted. Head back to the list." />;
  }

  return (
    <section className="animate-portal-rise">
      <BackLink to="/templates">All note templates</BackLink>
      <header className="mt-4">
        <PageEyebrow>NOTE TEMPLATE</PageEyebrow>
        <h1 className="text-portal-page tracking-[-1.7px]">{template ? template.name : 'New note template'}</h1>
        <p className="mt-1 text-portal-small text-portal-muted">
          {template ? (
            <>
              Followed by{' '}
              {template.usageCount > 0 ? (
                <Link to={`/templates/${template._id}/usage`} className="font-bold text-primary hover:underline">
                  {template.usageCount} note{template.usageCount === 1 ? '' : 's'}
                </Link>
              ) : (
                '0 notes'
              )}
              . Changing the fields bumps the version; existing notes keep what they have.
            </>
          ) : (
            'A status, the facts a note of this kind always carries, and a dated log underneath.'
          )}
        </p>
      </header>
      <TemplateForm key={template?._id ?? 'new'} template={template} />
    </section>
  );
}

/** A field row as edited; options are one per line. */
interface FieldDraft {
  readonly id: number;
  readonly key: string;
  readonly label: string;
  readonly type: TemplateFieldType;
  readonly options: string;
  readonly required: boolean;
  readonly hint: string;
  /** For a list field: the shape of one item. */
  readonly itemFields: readonly FieldDraft[];
}


/** "Speaker / Rep" → "speakerRep". */
function keyFromLabel(label: string): string {
  const words = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  return words.map((word, index) => (index === 0 ? word : word[0]!.toUpperCase() + word.slice(1))).join('');
}

let nextFieldId = 1;

/** An empty field row, ready to be named. */
function emptyDraft(): FieldDraft {
  return { id: nextFieldId++, key: '', label: '', type: 'text', options: '', required: false, hint: '', itemFields: [] };
}

function draftFrom(field: TemplateField): FieldDraft {
  return {
    id: nextFieldId++,
    key: field.key,
    label: field.label,
    type: field.type,
    options: (field.options ?? []).join('\n'),
    required: field.required ?? false,
    hint: field.hint ?? '',
    itemFields: (field.itemFields ?? []).map(draftFrom),
  };
}

/** A draft back to the wire shape, or null when it is incomplete. */
function fieldFromDraft(draft: FieldDraft): TemplateField | null {
  const key = draft.key.trim() || keyFromLabel(draft.label);
  if (!key || !draft.label.trim()) return null;
  const options = draft.options.split('\n').map((line) => line.trim()).filter(Boolean);
  return {
    key,
    label: draft.label.trim(),
    type: draft.type,
    required: draft.required,
    ...(draft.hint.trim() ? { hint: draft.hint.trim() } : {}),
    ...(draft.type === 'select' ? { options } : {}),
    ...(draft.type === 'list' ? { itemFields: draft.itemFields.map(fieldFromDraft).filter((item): item is TemplateField => item !== null) } : {}),
  };
}

function TemplateForm({ template }: { readonly template: Template | null }): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState(template?.name ?? '');
  const [key, setKey] = useState(template?.key ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [defaultSubject, setDefaultSubject] = useState(template?.defaultSubject ?? '');
  const [noteCategory, setNoteCategory] = useState(template?.appliesTo.noteCategory ?? '');
  const [programTags, setProgramTags] = useState((template?.appliesTo.programTags ?? []).join(', '));
  const [statusEnabled, setStatusEnabled] = useState(template?.status.enabled ?? false);
  const [statusOptions, setStatusOptions] = useState<readonly string[]>(template?.status.options ?? []);
  const [fields, setFields] = useState<readonly FieldDraft[]>(
    template ? template.fields.map(draftFrom) : [emptyDraft()],
  );
  const [logEnabled, setLogEnabled] = useState(template?.logEnabled ?? true);
  // The row Add field just made: brought into view and given focus once it mounts.
  const [focusId, setFocusId] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(template?.enabled ?? true);

  const parsedFields = fields.map(fieldFromDraft);
  const complete = parsedFields.filter((field): field is TemplateField => field !== null);
  const duplicateKeys = new Set(complete.map((field) => field.key)).size !== complete.length;
  const resolvedKey = (template ? template.key : key.trim() || keyFromLabel(name)).toLowerCase().replace(/[^a-z0-9-]/g, '-');
  const valid =
    name.trim().length > 0 &&
    resolvedKey.length > 0 &&
    complete.length === fields.length &&
    !duplicateKeys &&
    (!statusEnabled || statusOptions.length > 0);

  const save = useMutation({
    mutationFn: () => {
      const body: TemplateWrite = {
        key: resolvedKey,
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(defaultSubject.trim() ? { defaultSubject: defaultSubject.trim() } : {}),
        appliesTo: {
          ...(noteCategory ? { noteCategory } : {}),
          programTags: programTags.split(',').map((tag) => tag.trim()).filter(Boolean),
        },
        status: { enabled: statusEnabled, options: statusEnabled ? statusOptions : [] },
        fields: complete,
        logEnabled,
        enabled,
      };
      const { key: _key, ...rest } = body;
      return template ? updateTemplate(template._id, rest) : createTemplate(body);
    },
    onSuccess: async () => {
      toast.success(template ? `Saved ${name.trim()}` : `Created ${name.trim()}`);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      navigate('/templates');
    },
  });

  function updateField(id: number, patch: Partial<FieldDraft>): void {
    setFields((current) => current.map((field) => (field.id === id ? { ...field, ...patch } : field)));
  }
  function moveField(id: number, delta: -1 | 1): void {
    setFields((current) => {
      const index = current.findIndex((field) => field.id === id);
      const target = index + delta;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      return next;
    });
  }

  return (
    <form
      className="mt-6 space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !save.isPending) save.mutate();
      }}
    >
      <Panel className="space-y-4 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field guide="template-name" label="Name" htmlFor="template-name">
            <TextInput id="template-name" value={name} maxLength={80} placeholder="Charity" onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field guide="template-key" label="Key" htmlFor="template-key" hint={template ? 'Fixed once notes reference it.' : 'Letters, digits and dashes. Left blank, it comes from the name.'}>
            <TextInput
              id="template-key"
              value={template ? template.key : key}
              placeholder={keyFromLabel(name) || 'charity'}
              disabled={template !== null}
              onChange={(event) => setKey(event.target.value)}
            />
          </Field>
        </div>
        <Field guide="template-description" label="Description" htmlFor="template-description" hint="Shown under the template's name in the composer.">
          <TextArea id="template-description" rows={2} value={description} maxLength={500} onChange={(event) => setDescription(event.target.value)} />
        </Field>
        <Field guide="template-subject" label="Default subject" htmlFor="template-subject" hint="Put into the note's subject when this template is chosen. Anyone can change it on the note.">
          <TextInput id="template-subject" value={defaultSubject} maxLength={200} placeholder="Charity handover" onChange={(event) => setDefaultSubject(event.target.value)} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field guide="template-category" label="Offered for note category" htmlFor="template-category">
            <SelectInput id="template-category" value={noteCategory} onChange={(event) => setNoteCategory(event.target.value)}>
              <option value="">None — picked by hand</option>
              {NOTE_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {noteCategoryLabel(category)}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field guide="template-programs" label="Offered first for programs tagged" htmlFor="template-tags" hint="Comma separated, e.g. custom">
            <TextInput id="template-tags" value={programTags} placeholder="custom" onChange={(event) => setProgramTags(event.target.value)} />
          </Field>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-portal-small">
            <input type="checkbox" checked={logEnabled} onChange={(event) => setLogEnabled(event.target.checked)} />
            The note body is a dated running log
          </label>
          <label className="flex items-center gap-2 text-portal-small">
            <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
            Enabled
          </label>
        </div>
      </Panel>

      <Panel className="space-y-4 p-5">
        <label data-guide="template-has-status" className="flex items-center gap-2 text-portal-small font-bold">
          <input type="checkbox" checked={statusEnabled} onChange={(event) => setStatusEnabled(event.target.checked)} />
          Notes have a status
        </label>
        {statusEnabled ? (
          <ListField
            guide="template-statuses"
            label="Statuses, one per line"
            description="Shown as a pill on the note instead of typed into the subject."
            value={statusOptions}
            onChange={setStatusOptions}
            placeholder={'Pending\nConfirmed\nDeclined'}
            rows={5}
          />
        ) : null}
      </Panel>

      <Panel guide="template-fields" className="space-y-3 p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-portal-card-title font-bold">Fields</h2>
          <Button guide="template-add-field"
            type="button"
            variant="secondary"
            size="compact"
            onClick={() => {
              const draft = emptyDraft();
              setFocusId(draft.id);
              setFields((current) => [...current, draft]);
            }}
          >
            <PlusIcon aria-hidden className="size-4" /> Add field
          </Button>
        </div>
        {fields.length === 0 ? (
          <p className="text-portal-small text-portal-muted">No fields yet. A template with only a status and a log is fine.</p>
        ) : null}
        <div className="space-y-3">
          {fields.map((field, index) => (
            <FieldRow
              key={field.id}
              field={field}
              index={index}
              count={fields.length}
              focusOnMount={field.id === focusId}
              onChange={(patch) => updateField(field.id, patch)}
              onMove={(delta) => moveField(field.id, delta)}
              onRemove={() => setFields((current) => current.filter((row) => row.id !== field.id))}
            />
          ))}
        </div>
        {duplicateKeys ? <ErrorNotice inline title="Duplicate field keys" message="Every field needs its own key." /> : null}
      </Panel>

      {save.isError ? <ErrorNotice inline title="Template not saved" message={errorMessage(save.error)} /> : null}

      <div className="flex items-center justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => navigate('/templates')} disabled={save.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!valid || save.isPending}>
          {save.isPending ? 'Saving…' : template ? 'Save template' : 'Create template'}
        </Button>
      </div>
    </form>
  );
}

/**
 * One field's row in the editor: label, key, type, order, required, help
 * text, choices for a select, and for a list the item fields underneath,
 * edited with the same row. Items cannot be lists themselves.
 */
function FieldRow({
  field,
  index,
  count,
  onChange,
  onMove,
  onRemove,
  nested = false,
  focusOnMount = false,
}: {
  readonly field: FieldDraft;
  readonly index: number;
  readonly count: number;
  readonly onChange: (patch: Partial<FieldDraft>) => void;
  readonly onMove: (delta: -1 | 1) => void;
  readonly onRemove: () => void;
  /** Inside a list field: the type list has no list option, and the row sits tighter. */
  readonly nested?: boolean;
  /** A row just added: scroll it into view and put the cursor in its label. */
  readonly focusOnMount?: boolean;
}): React.ReactElement {
  const types = nested ? ITEM_FIELD_TYPES : TEMPLATE_FIELD_TYPES;
  const rowRef = useRef<HTMLDivElement>(null);
  const [nestedFocusId, setNestedFocusId] = useState<number | null>(null);
  useEffect(() => {
    // Syncing with the page's scroll position is the one thing only an effect can do here.
    if (!focusOnMount) return;
    rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    rowRef.current?.querySelector('input')?.focus({ preventScroll: true });
  }, [focusOnMount]);
  const setItemFields = (update: (current: readonly FieldDraft[]) => readonly FieldDraft[]): void => onChange({ itemFields: update(field.itemFields) });
  const commandButton = 'grid size-8 place-items-center text-portal-muted hover:bg-portal-bg hover:text-portal-ink disabled:opacity-30 disabled:hover:bg-transparent';
  return (
    <div ref={rowRef} className={`rounded-portal-button border border-portal-line p-4 ${nested ? 'bg-portal-bg' : 'bg-white'}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">
          {nested ? 'Item field' : 'Field'} {index + 1}
          {field.label.trim() ? ` · ${field.label.trim()}` : ''}
        </span>
        <div className="inline-flex divide-x divide-portal-line overflow-hidden rounded-portal-button border border-portal-line bg-white" role="group" aria-label={`Field ${index + 1} actions`}>
          <button type="button" aria-label="Move up" disabled={index === 0} onClick={() => onMove(-1)} className={commandButton}>
            <ArrowUpIcon aria-hidden className="size-4" />
          </button>
          <button type="button" aria-label="Move down" disabled={index === count - 1} onClick={() => onMove(1)} className={commandButton}>
            <ArrowDownIcon aria-hidden className="size-4" />
          </button>
          <button type="button" aria-label="Remove field" onClick={onRemove} className={`${commandButton} hover:text-rose-600`}>
            <XIcon aria-hidden className="size-4" />
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-[1fr_1fr_200px]">
        <Field guide="template-field-label" label="Label" htmlFor={`field-label-${field.id}`} hint="What the person filling in the note sees.">
          <TextInput
            id={`field-label-${field.id}`}
            value={field.label}
            placeholder="Speaker / Rep"
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </Field>
        <Field guide="template-field-key" label="Key" htmlFor={`field-key-${field.id}`} hint="How the value is stored. Letters and digits; filled in from the label if left blank.">
          <TextInput
            id={`field-key-${field.id}`}
            value={field.key}
            placeholder={keyFromLabel(field.label) || 'speakerRep'}
            onChange={(event) => onChange({ key: event.target.value })}
          />
        </Field>
        <Field guide="template-field-type" label="Type" htmlFor={`field-type-${field.id}`} hint="The kind of answer the field takes.">
          <SelectInput
            id={`field-type-${field.id}`}
            value={field.type}
            onChange={(event) => onChange({ type: event.target.value as TemplateFieldType })}
          >
            {types.map((type) => (
              <option key={type} value={type}>
                {TEMPLATE_FIELD_TYPE_LABELS[type]}
              </option>
            ))}
          </SelectInput>
        </Field>
      </div>

      <div className="mt-4">
        <Field guide="template-field-help" label="Help text" htmlFor={`field-hint-${field.id}`} hint="Shown under the field when someone fills it in.">
          <TextInput
            id={`field-hint-${field.id}`}
            value={field.hint}
            placeholder="The rep's cell, for the day of."
            onChange={(event) => onChange({ hint: event.target.value })}
          />
        </Field>
      </div>

      {field.type === 'select' ? (
        <div className="mt-4">
          <Field label="Choices" htmlFor={`field-options-${field.id}`} hint="One per line, in the order they are offered.">
            <TextArea
              id={`field-options-${field.id}`}
              rows={3}
              value={field.options}
              onChange={(event) => onChange({ options: event.target.value })}
            />
          </Field>
        </div>
      ) : null}

      <label data-guide="template-field-required" className="mt-4 flex items-start gap-2 text-portal-small">
        <input type="checkbox" className="mt-0.5" checked={field.required} onChange={(event) => onChange({ required: event.target.checked })} />
        <span>
          Required
          <span className="block text-portal-micro text-portal-muted">The note cannot be saved until this is filled in.</span>
        </span>
      </label>
      {field.type === 'list' ? (
        <div className="mt-3 border-t border-portal-line pt-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">Each item has</span>
            <Button
              type="button"
              variant="secondary"
              size="compact"
              onClick={() => {
                const draft = emptyDraft();
                setNestedFocusId(draft.id);
                setItemFields((current) => [...current, draft]);
              }}
            >
              <PlusIcon aria-hidden className="size-4" /> Add item field
            </Button>
          </div>
          {field.itemFields.length === 0 ? (
            <p className="text-portal-small text-portal-muted">No item fields yet. Add what one item is made of: a name, a quantity, a contact.</p>
          ) : (
            <div className="space-y-3">
              {field.itemFields.map((item, itemIndex) => (
                <FieldRow
                  key={item.id}
                  field={item}
                  index={itemIndex}
                  count={field.itemFields.length}
                  nested
                  focusOnMount={item.id === nestedFocusId}
                  onChange={(patch) => setItemFields((current) => current.map((row) => (row.id === item.id ? { ...row, ...patch } : row)))}
                  onMove={(delta) =>
                    setItemFields((current) => {
                      const from = current.findIndex((row) => row.id === item.id);
                      const to = from + delta;
                      if (from < 0 || to < 0 || to >= current.length) return current;
                      const next = [...current];
                      const [moved] = next.splice(from, 1);
                      next.splice(to, 0, moved as FieldDraft);
                      return next;
                    })
                  }
                  onRemove={() => setItemFields((current) => current.filter((row) => row.id !== item.id))}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
